import { Injectable } from '@angular/core';
import { ApiService } from '../api.service';
import { SharedDataService } from '../services/shared-data.service';
import { JobsListItem, JobItem } from '../objects/jobs';

const JOBS_REQUEST_INTERVAL = 5000;

const getItemName = file => file.split('/').pop();

@Injectable({
  providedIn: 'root'
})

export class BackgroundJobsService {

  private jobs: Array<string>;

  private requestInterval = null;

  constructor(private sharedDataService: SharedDataService, private apiService: ApiService) {
    window.addEventListener('beforeunload', () => {
      if (this.jobs.length) {
        window.sessionStorage.setItem('jobs', JSON.stringify(this.jobs)); // NOSONAR
      } else {
        window.sessionStorage.removeItem('jobs')
      }
    });
    this.jobs = JSON.parse(window.sessionStorage.getItem('jobs')) || [];
    if (this.jobs.length) {
      this.setupInterval();
    }
  }

  public addJob(job: string): void {
    this.jobs.push(job)
    this.setupInterval()
  }

  private clearInterval() {
    if (this.requestInterval) {
      clearInterval(this.requestInterval);
      this.requestInterval = null;
    }
  }
  private setupInterval() {
    if (!this.requestInterval) {
      this.requestInterval = setInterval(() => this.fetchJobs(), JOBS_REQUEST_INTERVAL)
      this.fetchJobs();
    }
  }

  public clearJobs() { // important to clear jobs when needed to prevent zombie jobs via: window.addEventListener('beforeunload'
    this.clearInterval();
    this.jobs = [];
  }

  private getErrorMessage(action, metadata, errorMsg) {
    const { files, path } = metadata;
    let msg;
    switch (action) {
      case 'copy':
        if (files.length > 1) {
          msg = `Copy of ${files.length} items to ${path} failed`
        } else {
          msg = `Copy of ${getItemName(files[0])} to ${path} failed`
        }
        break;
      case 'move':
        if (files.length > 1) {
          msg = `Move of ${files.length} items to ${path} failed`
        } else {
          msg = `Move of ${getItemName(files[0])} to ${path} failed`
        }
        break;
      case 'delete':
          if (files.length > 1) {
            msg = `Delete of ${files.length} items failed`
          } else {
            msg = `Delete of ${getItemName(files[0])} failed`
          }
          break;
      case 'unzip':
        msg = `Unzip of ${getItemName(path)} failed`
        break;
      case 'zip':
        msg = `Zip of ${getItemName(path)} failed`
        break;
      case 'snapshot_restore':
        msg = `Restore failed`
        break;
    }
    return errorMsg ? msg + `: ${errorMsg}` : msg
  }

  private getSuccessMessage(action, metadata) {
    const { files, path } = metadata;
    switch (action) {
      case 'copy':
        if (files.length > 1) {
          return `${files.length} items were copied to ${path}`
        } else {
          return `${getItemName(files[0])} was copied to ${path}`
        }
      case 'move':
        if (files.length > 1) {
          return `${files.length} items were moved to ${path}`
        } else {
          return `${getItemName(files[0])} was moved to ${path}`
        }
      case 'delete':
          if (files.length > 1) {
            return `${files.length} items were deleted successfully`
          } else {
            return `${getItemName(files[0])} was deleted successfully`
          }
      case 'unzip':
        return `${getItemName(path)} was unzipped successfully`
      case 'zip':
        return `${getItemName(path)} was created successfully`
      case 'snapshot_restore':
        if (metadata.length > 1) {
          return `${files.length} items were restored successfully`
        } else {
          return `${getItemName(metadata[0].from)} was restored to ${metadata[0].to}`
        }
    }
  }

  private showJobFinish(job: JobItem): void {
    const { status, name: action, notification: metadata, user_msg: errorMsg } = job;
    // Job success
    if (status === 2) {
      this.sharedDataService.pushNotification({ text: this.getSuccessMessage(action, metadata), type: 'success' })
      this.sharedDataService.jobResolved(job)
    }
    // Job failed
    if (status >= 3) {
      this.sharedDataService.pushNotification({ text: this.getErrorMessage(action, metadata, errorMsg), type: 'error' })
      this.sharedDataService.jobResolved(job)
    }
  }

  private fetchJobs() {
    if (!this.jobs.length) {
      this.clearInterval()
      return
    }

    this.apiService.getJobs(this.jobs)
      .subscribe(
        ({ jobs }) => this.handleJobsResponse(jobs),
        () => this.clearInterval())
  }

  private handleJobsResponse(jobs: Array<JobsListItem>): void {
    jobs.forEach(({ status, job_id }) => {
      if (status > 1) {
        this.jobs.splice(this.jobs.indexOf(job_id), 1)
        this.apiService.getJobInfo(job_id)
        .subscribe(job => this.showJobFinish(job))
      }
    })
    if (!this.jobs.length) {
      this.clearInterval()
    }
  }

}
